import React, { CSSProperties, FC } from 'react';
import Thumbnail from './Thumbnail';
import { Item } from '../../../../../__generated__/loma';
import mustache from 'mustache';
import placeholderImageUrl from './placeholderImageUrl';

const sanitizeUrl = (url: string): string => {
  if (!url) return '';
  try {
    return new URL(url).href;
  } catch {
    try {
      return encodeURI(url);
    } catch (e) {
      console.error('Error sanitizing imageurl', e);
      return '';
    }
  }
};

const renderOptions = { escape: sanitizeUrl };

export interface ProductThumbnailProps {
  imageUrlTemplate?: string | null;
  onClick?: () => void;
  productInfo: Pick<Item, 'url' | 'name' | 'imageUrl'>;
  urlTemplate?: string | null;
  renderAsLink?: boolean;
  s?: CSSProperties;
}

const productImageUrlRender = (
  productInfo: ProductThumbnailProps['productInfo'],
  imageUrlTemplate?: ProductThumbnailProps['imageUrlTemplate'],
): string => {
  // if imageUrlTemplate exists, assume retailer wants an image and
  // back up with a placeholder if itemInfo doesn't have an imageUrl
  const url = imageUrlTemplate
    ? mustache.render(
        imageUrlTemplate,
        productInfo,
        undefined,
        renderOptions,
      ) || placeholderImageUrl
    : productInfo.imageUrl || '';

  // reduce image url bugs due to malformed urls
  const sanitizedUrl = imageUrlTemplate ? url : sanitizeUrl(url);

  return sanitizedUrl;
};

export const imageWouldRenderEmpty = (
  productInfo: ProductThumbnailProps['productInfo'],
  imageUrlTemplate?: ProductThumbnailProps['imageUrlTemplate'],
): boolean => {
  const imageUrlRenderResult = productImageUrlRender(
    productInfo,
    imageUrlTemplate,
  );

  return !imageUrlRenderResult;
};

const ProductThumbnail: FC<ProductThumbnailProps> = ({
  imageUrlTemplate,
  onClick,
  productInfo,
  urlTemplate,
  renderAsLink = true,
  s = {},
}) => {
  // if imageUrlTemplate exists, assume retailer wants an image and
  // back up with a placeholder if itemInfo doesn't have an imageUrl
  const productImageUrl = productImageUrlRender(productInfo, imageUrlTemplate);

  const productUrl = urlTemplate
    ? mustache.render(urlTemplate, productInfo, undefined, renderOptions)
    : sanitizeUrl(productInfo.url || '');

  if (!productImageUrl) return null;

  const thumbnail = (
    <Thumbnail
      src={productImageUrl}
      alt={productInfo.name || ''}
      s={{
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
        width: '5.5rem',
        verticalAlign: 'top',
        borderRadius: 0,
        ...s,
      }}
    />
  );

  if (!renderAsLink || !productUrl) return thumbnail;

  return (
    <a
      href={productUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
    >
      {thumbnail}
    </a>
  );
};

export default ProductThumbnail;
